/**
 * EnterprisePricingPage.jsx
 * =========================
 * 
 * Page de configuration et paiement pour souscription entreprise.
 * 
 * RÈGLES STRICTES MOA:
 * - ❌ AUCUN calcul de prix côté frontend
 * - ✅ Le prix vient du backend via POST /api/company/calculate-price
 * 
 * P0 STABILITÉ:
 * - Initialisation UNIQUE avec verrou
 * - Pas de boucle de re-render
 * - États déterministes: INIT → READY
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, CreditCard, Plus, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { safeFetchJSON } from '../utils/apiHelpers';
import { getBackendUrl } from '../config/environment';
import EnterpriseSetupModal from '../components/enterprise/EnterpriseSetupModal';
import TokenCalculator from '../components/enterprise/TokenCalculator';

const BACKEND_URL = getBackendUrl();
const CONFIG_STORAGE_KEY = 'enterprise_checkout_config';

// ============================================================================
// HELPER: Lire config initiale (URL > localStorage > défaut)
// ============================================================================
function getInitialConfig(searchParams) {
  const urlLicences = searchParams.get('Licences');
  const urlDuration = searchParams.get('duration');
  const urlCompanyId = searchParams.get('company_id');
  
  // Priorité 1: URL params (retour Stripe)
  if (urlLicences || urlDuration) {
    return {
      Licences: urlLicences ? parseInt(urlLicences, 10) : 10,
      durationMonths: urlDuration ? parseInt(urlDuration, 10) : 12,
      companyId: urlCompanyId || null,
      source: 'url'
    };
  }
  
  // Priorité 2: localStorage
  try {
    const saved = localStorage.getItem(CONFIG_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Vérifier que la config n'est pas trop vieille (24h)
      if (parsed.timestamp && Date.now() - parsed.timestamp < 86400000) {
        return {
          Licences: parsed.Licences || 10,
          durationMonths: parsed.durationMonths || 12,
          companyId: parsed.companyId || null,
          source: 'localStorage'
        };
      }
    }
  } catch (e) {
    console.warn('[INIT] localStorage parse error:', e);
  }
  
  // Priorité 3: Défaut
  return { Licences: 10, durationMonths: 12, companyId: null, source: 'default' };
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const EnterprisePricingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ========== VERROU D'INITIALISATION ==========
  const isInitializedRef = useRef(false);
  const initialConfigRef = useRef(null);
  
  // Calculer config initiale UNE SEULE FOIS
  if (!initialConfigRef.current) {
    initialConfigRef.current = getInitialConfig(searchParams);
    console.log('[INIT] Configuration initiale:', initialConfigRef.current);
  }
  
  const isFromStripeCancel = searchParams.get('from') === 'stripe_cancel';
  
  // ========== ÉTATS ==========
  const [pageState, setPageState] = useState('INIT'); // INIT | READY | ERROR
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResumeMessage, setShowResumeMessage] = useState(isFromStripeCancel);
  
  // Config Licences/durée (initialisée depuis ref)
  const [Licences, setLicences] = useState(initialConfigRef.current.Licences);
  const [durationMonths, setDurationMonths] = useState(initialConfigRef.current.durationMonths);
  
  // Pricing
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  
  // Checkout
  const [processingCheckout, setProcessingCheckout] = useState(false);

  // ========== EFFET 1: Charger les entreprises (UNE SEULE FOIS) ==========
  useEffect(() => {
    if (isInitializedRef.current) return;
    
    const loadCompanies = async () => {
      console.log('[INIT] Loading companies...');
      setLoadingCompanies(true);
      
      try {
        const token = localStorage.getItem('token');
        const data = await safeFetchJSON(`${BACKEND_URL}/api/company/my-companies`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const companiesList = data.companies || [];
        setCompanies(companiesList);
        
        // Sélectionner l'entreprise
        if (companiesList.length > 0) {
          const targetCompanyId = initialConfigRef.current.companyId;
          let companyToSelect = null;
          
          if (targetCompanyId) {
            companyToSelect = companiesList.find(c => c.id === targetCompanyId);
          }
          
          if (!companyToSelect) {
            companyToSelect = companiesList[0];
          }
          
          setSelectedCompany(companyToSelect);
          console.log('[INIT] Company selected:', companyToSelect?.name);
        }
      } catch (error) {
        console.error('[INIT] Error loading companies:', error);
        setPageState('ERROR');
      } finally {
        setLoadingCompanies(false);
        isInitializedRef.current = true;
        setPageState('READY');
        console.log('[READY] Page stabilized');
      }
    };
    
    loadCompanies();
  }, []); // Dépendances VIDES = exécution unique

  // ========== EFFET 2: Calcul du prix (debounced) ==========
  useEffect(() => {
    // Ne pas calculer si pas encore initialisé ou pas de config valide
    if (pageState !== 'READY' || Licences <= 0 || durationMonths <= 0) return;
    
    const controller = new AbortController();
    
    const calculatePrice = async () => {
      setLoadingPricing(true);
      
      try {
        const authToken = localStorage.getItem('token');
        const data = await safeFetchJSON(`${BACKEND_URL}/api/company/calculate-price`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            Licences: Licences,
            duration_months: durationMonths
          }),
          signal: controller.signal
        });
        
        setPricing(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[PRICING] Error:', error);
          setPricing(null);
        }
      } finally {
        setLoadingPricing(false);
      }
    };
    
    // Debounce 300ms
    const timer = setTimeout(calculatePrice, 300);
    
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [pageState, Licences, durationMonths]);

  // ========== EFFET 3: Nettoyer les params URL après init ==========
  useEffect(() => {
    if (pageState === 'READY' && isFromStripeCancel) {
      // Nettoyer l'URL après 1 seconde pour ne pas polluer l'historique
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [pageState, isFromStripeCancel, setSearchParams]);

  // ========== HANDLERS ==========
  const handleCheckout = async () => {
    // 🔒 P1 MOA (Jan 2026): Validation uniquement à la soumission
    const licencesValue = parseInt(Licences) || 0;
    const durationValue = parseInt(durationMonths) || 0;
    
    if (licencesValue < 1) {
      toast.error('Veuillez saisir au moins 1 licence');
      return;
    }
    
    if (durationValue < 1) {
      toast.error('Veuillez saisir au moins 1 mois de durée');
      return;
    }
    
    if (!selectedCompany || !pricing) return;
    
    setProcessingCheckout(true);
    
    // Persister config AVANT redirection
    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify({
      companyId: selectedCompany.id,
      Licences: licencesValue,
      durationMonths: durationValue,
      timestamp: Date.now()
    }));
    
    try {
      const authToken = localStorage.getItem('token');
      const data = await safeFetchJSON(`${BACKEND_URL}/api/company/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          company_id: selectedCompany.id,
          Licences: licencesValue,
          duration_months: durationValue,
          // 🔧 TEMPORAIRE PREVIEW: Utiliser l'URL de preview pour tester
          // TODO: Remettre "https://coachdia.com" après validation
          origin_url: window.location.origin
        })
      });
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error) {
      console.error('[CHECKOUT] Error:', error);
      toast.error(error.message || 'Erreur lors de la création du paiement');
      setProcessingCheckout(false);
    }
  };

  const handleCompanyCreated = (newCompany) => {
    setCompanies(prev => [...prev, newCompany]);
    setSelectedCompany(newCompany);
    setShowCreateModal(false);
    toast.success('Entreprise créée avec succès');
  };

  const handleCloseResumeMessage = () => {
    setShowResumeMessage(false);
  };

  // ========== RENDER ==========
  return (
    <div className="theme-light page-with-sticky-header-safe">
      {/* Header sticky avec compensation safe-area */}
      <div className="sticky-header-safe bg-[#F8F7F4]/95 backdrop-blur-lg border-b border-[#E8E5DE]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#6B7280] hover:text-[#111827] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Retour</span>
          </button>
          <div className="text-[#111827] font-semibold">Offre Entreprise</div>
          <div className="w-20" />
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto px-4 py-8 page-content-scrollable">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 bg-[#F5F0E3] px-4 py-2 rounded-full mb-4">
            <Building2 className="w-5 h-5 text-[#92710A]" />
            <span className="text-[#7A5F0A] font-medium">Offre Entreprise</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111827] mb-4">
            Configurez votre{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#92710A]">
              offre sur mesure
            </span>
          </h1>
        </motion.div>

        {/* Message de reprise Stripe */}
        {showResumeMessage && pageState === 'READY' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#F8F7F4] border border-[#E8DFC8] rounded-xl p-4 mb-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-[#92710A]" />
              <div>
                <p className="text-[#4B5563] font-medium">Reprenez votre configuration</p>
                <p className="text-[#6B7280] text-sm">Votre sélection a été conservée.</p>
              </div>
            </div>
            <button
              onClick={handleCloseResumeMessage}
              className="text-[#92710A] hover:text-[#7A5F0A] p-2"
              aria-label="Fermer"
            >
              ✕
            </button>
          </motion.div>
        )}

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl p-6 md:p-8 border border-[#E8E5DE] shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_20px_rgba(0,0,0,0.04)]"
        >
          {/* Section 1: Entreprise */}
          <div className="mb-8">
            <label className="block text-[#111827] font-semibold mb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#92710A]" />
              Mon entreprise <span className="text-red-400">*</span>
            </label>
            
            {loadingCompanies ? (
              <div className="flex items-center gap-2 text-[#6B7280] py-3">
                <Loader2 className="w-5 h-5 animate-spin" />
                Chargement des entreprises...
              </div>
            ) : (
              <div className="flex gap-3">
                <select
                  value={selectedCompany?.id || ''}
                  onChange={(e) => {
                    const company = companies.find(c => c.id === e.target.value);
                    setSelectedCompany(company || null);
                  }}
                  className={`flex-1 bg-[#F8F7F4] border rounded-xl px-4 py-3 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                    !selectedCompany ? 'border-amber-300' : 'border-[#DDD9D0]'
                  }`}
                  data-testid="company-select"
                >
                  <option value="" className="text-[#111827]">
                    {companies.length === 0 ? 'Aucune entreprise' : 'Sélectionner...'}
                  </option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id} className="text-[#111827]">
                      {company.name}
                    </option>
                  ))}
                </select>
                
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-3 bg-[#F5F0E3] hover:bg-[#F5F0E3] border border-[#E8DFC8] rounded-xl text-[#7A5F0A] font-medium flex items-center gap-2 transition-colors"
                  data-testid="create-company-btn"
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden sm:inline">Créer</span>
                </button>
              </div>
            )}
            
            {/* Message obligation */}
            {!loadingCompanies && !selectedCompany && (
              <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-700 font-medium">Champ obligatoire</p>
                  <p className="text-xs text-amber-600">Sélectionnez ou créez une entreprise.</p>
                </div>
              </div>
            )}
            
            {/* Info entreprise sélectionnée */}
            {selectedCompany && (
              <div className="mt-3 p-3 bg-[#F5F0E3] rounded-lg border border-[#E8DFC8]">
                <p className="text-sm text-[#4B5563]">
                  <strong>{selectedCompany.name}</strong> — {selectedCompany.billing_email}
                </p>
                <p className="text-xs text-gray-300 mt-1">
                  Licences : {selectedCompany.Licences_available || 0} / {selectedCompany.Licences_purchased || 0}
                </p>
              </div>
            )}
          </div>

          {/* Section 2: Configuration */}
          <TokenCalculator
            Licences={Licences}
            setLicences={setLicences}
            durationMonths={durationMonths}
            setDurationMonths={setDurationMonths}
            pricing={pricing}
            loadingPricing={loadingPricing}
          />

          {/* Section 3: Checkout */}
          <div className="text-center mt-8">
            <button
              onClick={handleCheckout}
              disabled={!selectedCompany || !pricing || processingCheckout || loadingPricing}
              data-testid="checkout-btn"
              className={`w-full md:w-auto px-12 py-4 text-[#111827] font-bold text-lg rounded-xl shadow-xl transition-all flex items-center justify-center gap-3 mx-auto ${
                processingCheckout
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#C49B2A]'
                  : !selectedCompany
                    ? 'bg-gradient-to-r from-amber-200 to-amber-300 cursor-not-allowed'
                    : loadingPricing
                      ? 'bg-gradient-to-r from-gray-300 to-gray-400 cursor-wait'
                      : !pricing
                        ? 'bg-gradient-to-r from-gray-300 to-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#D4AF37] to-[#C49B2A] hover:from-[#C49B2A] hover:to-[#B38D22]'
              }`}
            >
              {processingCheckout ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Redirection...
                </>
              ) : loadingPricing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Calcul du prix...
                </>
              ) : !selectedCompany ? (
                <>
                  <Building2 className="w-5 h-5" />
                  Sélectionnez une entreprise
                </>
              ) : !pricing ? (
                <>
                  <AlertCircle className="w-5 h-5" />
                  Configuration requise
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Procéder au paiement
                </>
              )}
            </button>
            
            {!selectedCompany && !loadingCompanies && (
              <p className="text-amber-600 text-sm mt-3 flex items-center justify-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Sélectionnez une entreprise pour continuer
              </p>
            )}
            
            {selectedCompany && pricing && (
              <p className="text-[#6B7280] text-sm mt-4">
                🔒 Paiement 100% sécurisé par Stripe
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Modal création entreprise */}
      {showCreateModal && (
        <EnterpriseSetupModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCompanyCreated}
        />
      )}
    </div>
  );
};

export default EnterprisePricingPage;
